import React from 'react';
import { Button, SelectLabel, Section, InputLabel, Input, Message, Icon, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../../attendee/detail/components/utils';
import UploadRequestConfigModel from '../../../../models/UploadRequestConfig';
import UploadFileZone from '../../../../components/upload/uploadFileZone';
import css from './imports-steps.module.css';

export const FileStep = ({ organization, event, defaultOrigin, distributors, loadingDistributors, form, setForm, setFormObject, onCardDone, syncDistributor }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [disabledSelectLabel, setDisabledSelectLabel] = React.useState(form?.importRequest ? true : false);

    const originOptions = React.useMemo(() => {
        let options = [
            {
                text: (
                    <span className={css['icon_select_option']}>
                        <Icon name="plus" />
                        {t('ticket.attendees.imports.steps.file.manual-origin.option')}
                    </span>
                ),
                value: 'newOrigin',
                key: 'newOrigin'
            }
        ];
        distributors
            ?.filter(dst => dst.id === defaultOrigin || dst.organization) // Filter only default and custom distributors
            .map(dst => {
                return options.push({
                    text: capitalizeFirstLetter(dst.label),
                    value: dst.id,
                    key: dst.id
                });
            });
        return options;
    }, [distributors, loadingDistributors, defaultOrigin]);

    const importRequestPayload = React.useMemo(() => {
        let datas = {
            event_id: event.pk,
            step_name: 'creation'
        };
        if (!form.newOrigin) {
            datas['distributor_id'] = form.origin;
        } else {
            datas['distributor_name'] = form.newOrigin;
        }
        return datas;
    }, [form, event]);

    const handleDownload = React.useCallback(() => {
        const fileName = event.is_seated ? 'template_import_with_seat.xlsx' : 'template_import_no_seat.xlsx';
        const link = document.createElement('a');
        link.download = fileName;
        link.href = `/ticket/static/${fileName}`;
        link.click();
    }, [event]);

    const displayErrorMessage = React.useMemo(() => {
        return event.is_seated || event.use_sessions;
    }, [event]);

    const disabled = React.useMemo(() => {
        switch (true) {
            case displayErrorMessage:
            case !form.origin:
            case form.origin === 'newOrigin' && !form.newOrigin:
            case Array.isArray(form.file) && !form.file.length:
            case !form.importRequest:
                return true;
            default:
                return false;
        }
    }, [form, displayErrorMessage]);

    return (
        <div className={css['step-body']}>
            {displayErrorMessage && (
                <Message type="danger" title={t('ticket.attendees.imports.steps.file.error_seated_session_event_title')}>
                    {t('ticket.attendees.imports.steps.file.error_seated_session_event_message')}
                </Message>
            )}

            <SelectLabel
                useNew
                label={t('ticket.common.origin')}
                options={originOptions}
                onChange={value => setForm('origin', value)}
                placeholder={t('ticket.manual_import.title')}
                value={form.origin}
                disabled={disabledSelectLabel}
                onClear={() => setForm('origin', null)}
            />
            {form.origin === 'newOrigin' && (
                <Section>
                    <InputLabel label={t('ticket.attendees.imports.steps.file.manual-origin.label')} />
                    <Input
                        disabled={disabledSelectLabel}
                        value={form.newOrigin}
                        placeholder={t('ticket.attendees.imports.steps.file.manual-origin.placeholder')}
                        onChange={({ target: { value } }) => setForm('newOrigin', value)}
                    />
                </Section>
            )}
            <div className={css['step-upload-block']}>
                <div>{t('ticket.attendees.imports.steps.file.upload.label')}</div>
                <UploadFileZone
                    fileTypes={['.xlsx']}
                    file={form.file?.path}
                    organization={organization}
                    customErrors={
                        <Message className={css['file-error-message']} title={t('nacre.ImportController.analysis.header.error')} type="danger">
                            {t('ticket.charts.newZone.fileError.seatMap', {
                                format: '.xlsx'
                            })}
                        </Message>
                    }
                    onError={() => {
                        setFormObject({ ...form, file: [], importRequest: [], requestDatas: {} });
                        toast.error(t('ticket.common.toast_error'));
                    }}
                    onDelete={() => {
                        setFormObject({ ...form, file: [], importRequest: [], requestDatas: {}, origin: defaultOrigin, newOrigin: '' });
                        setDisabledSelectLabel(false);
                        syncDistributor();
                    }}
                    onSelect={file => {
                        setForm('file', file);
                    }}
                    onUpload={resp => {
                        setDisabledSelectLabel(true);
                        setForm('importRequest', resp);
                    }}
                    getURLParams={file => ({
                        eventId: event.pk,
                        datas: importRequestPayload,
                        name: file?.name?.split('.')[0]
                    })}
                    uploadConfigModel={UploadRequestConfigModel}
                    labelDropzone={t('ticket.attendees.imports.steps.file.upload.description')}
                />
            </div>
            <div className={css['step-footer']}>
                <Button className={css['step-cta']} primary label={t('ticket.common.action.continue')} onClick={onCardDone} disabled={disabled} />
                <Button primary inverted label={t('ticket.attendees.imports.steps.file.upload.template')} onClick={handleDownload} />
            </div>
        </div>
    );
};
